import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { IFigureDto } from '../models/IFigureDto';
import type { IFigureCollection } from '../models/IFigureCollection';
import { FavoritesContext } from '../context/FavoritesContext';
import { useConfig } from '../config/env';
import FigureService from '../services/figureService';
import { getCollectionColors } from '../utils/collectionColors';
import type { StatusCode } from '../components/StatusChip';
import { emitAppEvent } from '../events/appEvents';
import { useAppEvent } from './useAppEvent';

export interface VersionHistory {
  versionId: number;
  docId: number;
  guideVersionPublicId: string;
  title: string;
  publishedYear: number;
  publishedDate: string;
  published: boolean;
}

interface UseFigureDetailStateParams {
  initialFigure: IFigureDto;
  collectionId: number;
  collections: Array<{ id: number; name: string; color?: string }>;
  onAvatarClick?: (collectionId: number) => void;
}

export interface UseFigureDetailStateResult {
  figure: IFigureDto;
  setFigure: React.Dispatch<React.SetStateAction<IFigureDto>>;
  isFav: boolean;
  handleFavoriteClick: () => void;
  versionHistory: VersionHistory[];
  isZoomed: boolean;
  setIsZoomed: (value: boolean) => void;
  forceExpanded: boolean;
  setForceExpanded: (value: boolean) => void;
  selectedCollectionTab: string | false;
  setSelectedCollectionTab: (value: string | false) => void;
  newCollectionName: string;
  setNewCollectionName: (value: string) => void;
  collectionMenuAnchor: HTMLElement | null;
  setCollectionMenuAnchor: (el: HTMLElement | null) => void;
  availableCollections: IFigureCollection[];
  collectionFieldRef: React.RefObject<HTMLDivElement>;
  popoverContentRef: React.RefObject<HTMLDivElement>;
  handleAddButtonClick: (event: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
  handleCollectionMenuClose: (event?: unknown | React.MouseEvent | React.KeyboardEvent, reason?: 'backdropClick' | 'escapeKeyDown') => void;
  handleCollectionSelect: (selectedCollectionId: number) => Promise<void>;
  handleAddToCollection: () => Promise<void>;
  safeStatus: StatusCode;
  breadcrumbItems: Array<{ label: string; href?: string; onClick?: () => void }>;
}

export function useFigureDetailState({
  initialFigure,
  collectionId,
  collections,
  onAvatarClick,
}: UseFigureDetailStateParams): UseFigureDetailStateResult {
  const [figure, setFigure] = useState<IFigureDto>(initialFigure);
  const { toggleFavorite, isFavorite } = useContext(FavoritesContext);
  const cfg = useConfig();
  const queryClient = useQueryClient();

  const [newCollectionName, setNewCollectionName] = useState('');
  const [collectionMenuAnchor, setCollectionMenuAnchor] = useState<null | HTMLElement>(null);
  const [availableCollections, setAvailableCollections] = useState<IFigureCollection[]>([]);
  const collectionFieldRef = useRef<HTMLDivElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  const [versionHistory, setVersionHistory] = useState<VersionHistory[]>([]);
  const [isZoomed, setIsZoomed] = useState(false);
  const [forceExpanded, setForceExpanded] = useState(false);
  const [selectedCollectionTab, setSelectedCollectionTab] = useState<string | false>(false);
  const lastCollectionIdRef = useRef<number>(collectionId);

  const isFav = isFavorite(figure.imageId.toString());

  const loadCollectionsForMenu = useCallback(async () => {
    try {
      const customerId = 1; // TODO: get from context or props
      const allCollections = await FigureService.getCollectionsByCustomer(customerId);
      const favoriteId = Number(cfg?.favoritterCollectionId ?? 0);
      const defaultId = Number(cfg?.defaultCollectionId ?? 0);

      let defaultCollection = allCollections.find((c) =>
        defaultId > 0 ? c.id === defaultId : (c as IFigureCollection & { isDefault?: boolean }).isDefault === true,
      );
      if (defaultCollection && defaultCollection.id === favoriteId) {
        defaultCollection = undefined;
      }

      const otherCollections = allCollections.filter((c) => c.id !== favoriteId && (!defaultCollection || c.id !== defaultCollection.id));
      const orderedCollections = defaultCollection ? [defaultCollection, ...otherCollections] : otherCollections;

      // eslint-disable-next-line no-console
      console.log('Loaded collections for menu:', orderedCollections.length, 'out of', allCollections.length, 'total');
      // eslint-disable-next-line no-console
      console.log(
        'Available collections:',
        orderedCollections.map((c) => ({ id: c.id, name: c.name })),
      );
      // eslint-disable-next-line no-console
      console.log('Figure memberships:', figure.collectionMemberships);
      setAvailableCollections(orderedCollections);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching collections:', error);
    }
  }, [cfg?.defaultCollectionId, cfg?.favoritterCollectionId, figure.collectionMemberships]);

  const handleAddButtonClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (collectionFieldRef.current) {
        await loadCollectionsForMenu();
        setCollectionMenuAnchor(collectionFieldRef.current);
      }
    },
    [loadCollectionsForMenu],
  );

  const handleCollectionMenuClose = useCallback(
    (event?: unknown | React.MouseEvent | React.KeyboardEvent, _reason?: 'backdropClick' | 'escapeKeyDown') => {
      if (event && typeof event === 'object' && event !== null && 'target' in event && popoverContentRef.current) {
        const target = (event as { target?: unknown }).target;
        if (target && popoverContentRef.current.contains(target as Node)) {
          return;
        }
      }
      setCollectionMenuAnchor(null);
    },
    [],
  );

  const handleCollectionSelect = useCallback(
    async (selectedCollectionId: number) => {
      handleCollectionMenuClose();

      try {
        if (figure.collectionMemberships[selectedCollectionId]) {
          setNewCollectionName('');
          return;
        }

        await FigureService.addFigureToCollection(selectedCollectionId, figure.imageId);

        queryClient.invalidateQueries({ queryKey: ['figures-grid', selectedCollectionId] });
        queryClient.invalidateQueries({ queryKey: ['figures-grid', collectionId] });
        queryClient.invalidateQueries({ queryKey: ['figures', collectionId] });

        const fresh = await FigureService.getFigureByIdAndCollection(figure.imageId, collectionId);
        emitAppEvent('figureUpdated', fresh);
        setFigure(fresh);

        setNewCollectionName('');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error adding figure to collection:', error);
      }
    },
    [collectionId, figure.collectionMemberships, figure.imageId, handleCollectionMenuClose, queryClient],
  );

  const handleAddToCollection = useCallback(async () => {
    const trimmedName = newCollectionName.trim();
    if (!trimmedName) return;

    try {
      const customerId = 1; // TODO: get from context or props

      const allCollections = await FigureService.getCollectionsByCustomer(customerId);
      const existingCollection = allCollections.find((c) => c.name.toLowerCase() === trimmedName.toLowerCase());

      let targetCollectionId: number;

      if (existingCollection) {
        targetCollectionId = existingCollection.id;

        if (figure.collectionMemberships[targetCollectionId]) {
          setNewCollectionName('');
          return;
        }
      } else {
        const newCollection = await FigureService.addCollection(trimmedName, customerId, '');

        const derived = getCollectionColors({ id: String(newCollection.id), name: newCollection.name });
        const savedWithColor = await FigureService.updateCollectionDetails(
          newCollection.id,
          newCollection.name,
          customerId,
          newCollection.comment ?? '',
          newCollection.headerComment ?? '',
          derived.bg,
        );

        targetCollectionId = savedWithColor.id;

        emitAppEvent('collectionCreated', savedWithColor);
      }

      await FigureService.addFigureToCollection(targetCollectionId, figure.imageId);

      queryClient.invalidateQueries({ queryKey: ['figures-grid', targetCollectionId] });
      queryClient.invalidateQueries({ queryKey: ['figures-grid', collectionId] });
      queryClient.invalidateQueries({ queryKey: ['figures', collectionId] });

      const fresh = await FigureService.getFigureByIdAndCollection(figure.imageId, collectionId);
      emitAppEvent('figureUpdated', fresh);
      setFigure(fresh);

      setNewCollectionName('');
      handleCollectionMenuClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding figure to collection:', error);
    }
  }, [collectionId, figure.collectionMemberships, figure.imageId, handleCollectionMenuClose, newCollectionName, queryClient]);

  const handleFavoriteClick = useCallback(() => {
    toggleFavorite(figure.imageId.toString(), collectionId);
  }, [collectionId, figure.imageId, toggleFavorite]);

  useEffect(() => {
    let cancelled = false;
    const fetchFullFigure = async () => {
      try {
        if (!initialFigure?.imageId) return;
        const fresh = await FigureService.getFigureByIdAndCollection(initialFigure.imageId, collectionId);
        if (!cancelled && fresh) setFigure(fresh);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch full figure:', err);
      }
    };
    void fetchFullFigure();
    return () => {
      cancelled = true;
    };
  }, [initialFigure?.imageId, collectionId]);

  useAppEvent('figureUpdated', (incoming) => {
    setFigure((prev) => {
      if (incoming.imageId !== prev.imageId) return prev;
      const fav = isFavorite(incoming.imageId.toString());
      const merged: IFigureDto = {
        ...prev,
        ...incoming,
        metadata: incoming.metadata ?? prev.metadata,
        customerFavorite: fav ? true : incoming.customerFavorite,
      } as IFigureDto;
      // eslint-disable-next-line no-console
      console.log(`FigureDetail: Updated figure ${merged.imageId}, customerFavorite=${merged.customerFavorite}`);
      return merged;
    });
  });

  useAppEvent('figureDataUpdated', (updatedFigures) => {
    setFigure((prev) => {
      const incoming = updatedFigures.find((f) => f.imageId === prev.imageId);
      if (!incoming) return prev;
      const fav = isFavorite(incoming.imageId.toString());
      const merged: IFigureDto = {
        ...prev,
        ...incoming,
        metadata: incoming.metadata ?? prev.metadata,
        customerFavorite: fav ? true : incoming.customerFavorite,
      } as IFigureDto;
      return merged;
    });
  });

  useAppEvent('figureRemoved', ({ removedFigureId, collectionId: removedFrom }) => {
    setFigure((prev) => {
      if (removedFigureId !== prev.imageId) return prev;
      const next = { ...prev, collectionMemberships: { ...prev.collectionMemberships } };
      delete next.collectionMemberships[Number(removedFrom)];
      return next;
    });
  });

  useEffect(() => {
    setFigure(initialFigure);
  }, [initialFigure]);

  useEffect(() => {
    const defaultId = Number(cfg?.defaultCollectionId ?? 0);
    const favoriteId = Number(cfg?.favoritterCollectionId ?? 0);
    const defaultCollectionIdStr = defaultId > 0 ? String(defaultId) : null;
    const allCollectionIds = new Set(Object.keys(figure.collectionMemberships));
    if (defaultCollectionIdStr) {
      allCollectionIds.add(defaultCollectionIdStr);
    }

    const collectionIdChanged = lastCollectionIdRef.current !== collectionId;
    if (collectionIdChanged) {
      lastCollectionIdRef.current = collectionId;
    }

    if (!collectionIdChanged && selectedCollectionTab && typeof selectedCollectionTab === 'string' && allCollectionIds.has(selectedCollectionTab)) {
      return;
    }

    const effectiveCollectionId = collectionId === favoriteId ? defaultId : collectionId;

    if (effectiveCollectionId > 0 && allCollectionIds.has(String(effectiveCollectionId))) {
      setSelectedCollectionTab(String(effectiveCollectionId));
    } else if (defaultCollectionIdStr && allCollectionIds.has(defaultCollectionIdStr)) {
      setSelectedCollectionTab(defaultCollectionIdStr);
    } else if (allCollectionIds.size > 0) {
      const sortedIds = Array.from(allCollectionIds).sort((idA, idB) => {
        const isDefaultA = defaultId > 0 && Number(idA) === defaultId;
        const isDefaultB = defaultId > 0 && Number(idB) === defaultId;
        if (isDefaultA && !isDefaultB) return -1;
        if (!isDefaultA && isDefaultB) return 1;
        return 0;
      });
      setSelectedCollectionTab(sortedIds[0]);
    }
  }, [collectionId, figure.collectionMemberships, cfg?.defaultCollectionId, cfg?.favoritterCollectionId, selectedCollectionTab]);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const history = await FigureService.getVersionHistory(figure.docId);
        setVersionHistory(history);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load version history:', error);
      }
    };

    if (figure.docId) {
      void loadHistory();
    }
  }, [figure.docId]);

  const safeStatus = ([0, 1, 2, 3, 4].includes(figure.status) ? figure.status : 0) as StatusCode;

  const favoriteId = Number(cfg?.favoritterCollectionId ?? 0);
  const collectionName = collectionId === favoriteId ? 'Favoritter' : collections.find((c) => c.id === collectionId)?.name || '';
  const defaultId = Number(cfg?.defaultCollectionId ?? 0);
  const defaultCollectionName = collections.find((c) => c.id === defaultId)?.name || 'Byggforskserien';
  const isDefaultCollection = collectionId === defaultId;

  const breadcrumbItems: Array<{ label: string; href?: string; onClick?: () => void }> = [
    {
      label: defaultCollectionName,
      onClick: onAvatarClick && defaultId > 0 ? () => onAvatarClick(defaultId) : undefined,
    },
    ...(isDefaultCollection
      ? []
      : [
          {
            label: collectionName,
            onClick: onAvatarClick ? () => onAvatarClick(collectionId) : undefined,
          },
        ]),
  ];

  return {
    figure,
    setFigure,
    isFav,
    handleFavoriteClick,
    versionHistory,
    isZoomed,
    setIsZoomed,
    forceExpanded,
    setForceExpanded,
    selectedCollectionTab,
    setSelectedCollectionTab,
    newCollectionName,
    setNewCollectionName,
    collectionMenuAnchor,
    setCollectionMenuAnchor,
    availableCollections,
    collectionFieldRef,
    popoverContentRef,
    handleAddButtonClick,
    handleCollectionMenuClose,
    handleCollectionSelect,
    handleAddToCollection,
    safeStatus,
    breadcrumbItems,
  };
}
